/**
 * Walkable interiors.
 *
 * **Why they are not inside the buildings.** The exterior buildings are solid
 * boxes with no openings cut in them — a constraint this codebase has run into
 * twice already, once when shopfront interiors had to be painted rather than
 * modelled, and once when window reveals had to be built outward. Cutting real
 * rooms into them would mean rebuilding every facade as a shell.
 *
 * So an interior is a separate room standing on its own, placed well outside
 * the world bounds where nothing else is. Entering teleports the player there
 * and swaps the collider set and the movement bounds; the room has a ceiling,
 * so the outside world is never visible from within it. This is the oldest
 * trick in the genre and it costs a fraction of what the honest version would.
 *
 * Everything here is one function per room returning the same shape, so a
 * future apartment or a customer's house is a new `InteriorDef` and nothing
 * else.
 */
import * as THREE from 'three';
import { standard } from './Materials.js';
import { MeshBatcher } from './MeshBatcher.js';
import type { Collider } from './Neighbourhood.js';
import { buildHumanoid } from './Humanoid.js';

/** Interiors live out here, far outside `WORLD_BOUNDS`, one per slot. */
export const INTERIOR_ORIGIN_X = 600;
const SLOT_SPACING = 120;

export type InteriorKind = 'home_room' | 'tool_shop';

/** A point inside a room that the player can do something at. */
export interface InteriorHotspot {
  /** Which interaction this stands in for, or 'exit' for the door. */
  kind: 'exit' | 'sleep' | 'shop_tools' | 'storage';
  x: number;
  z: number;
  radius: number;
  label: string;
}

export interface InteriorDef {
  kind: InteriorKind;
  /** The exterior location this room belongs to. */
  locationId: string;
  group: THREE.Group;
  colliders: Collider[];
  /** Movement is clamped to this while inside. */
  bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
  /** Where the player appears, and which way they face. */
  spawn: { x: number; z: number; facing: number };
  hotspots: InteriorHotspot[];
  /** How far the camera may sit back in this room. */
  cameraDistance: number;
  /** Lights that need to exist while the room is shown. */
  lights: THREE.Object3D[];
}

export interface InteriorSet {
  group: THREE.Group;
  byLocation: Map<string, InteriorDef>;
  /** Show only the room being used; the others cost nothing while hidden. */
  setActive(locationId: string | null): void;
}

export function createInteriors(): InteriorSet {
  const group = new THREE.Group();
  const byLocation = new Map<string, InteriorDef>();

  const defs = [
    buildHomeRoom(INTERIOR_ORIGIN_X, 0),
    buildToolShop(INTERIOR_ORIGIN_X + SLOT_SPACING, 0),
  ];
  for (const def of defs) {
    def.group.visible = false;
    group.add(def.group);
    byLocation.set(def.locationId, def);
  }

  return {
    group,
    byLocation,
    setActive(locationId: string | null): void {
      for (const [id, def] of byLocation) def.group.visible = id === locationId;
    },
  };
}

// ---------------------------------------------------------------------------
// Shared construction
// ---------------------------------------------------------------------------

interface ShellOptions {
  width: number;
  depth: number;
  height: number;
  floor: THREE.Material;
  wall: THREE.Material;
  ceiling: THREE.Material;
  /** Gap in the -Z wall for the door, in metres. */
  doorWidth: number;
}

/**
 * Floor, ceiling and four walls with a doorway.
 *
 * The doorway is a gap made of two wall pieces rather than a hole cut in one,
 * for the same reason the exterior windows are: this project has no CSG, and
 * two boxes with a space between them is what a doorway is anyway.
 */
function buildShell(
  root: THREE.Group,
  colliders: Collider[],
  originX: number,
  originZ: number,
  o: ShellOptions,
): void {
  const hw = o.width / 2;
  const hd = o.depth / 2;
  const batcher = new MeshBatcher();

  batcher.add(new THREE.BoxGeometry(o.width, 0.18, o.depth), o.floor,
    { position: [originX, -0.09, originZ] });
  batcher.add(new THREE.BoxGeometry(o.width, 0.16, o.depth), o.ceiling,
    { position: [originX, o.height, originZ] });

  const t = 0.22;
  const walls: [number, number, number, number][] = [
    // width, depth, offsetX, offsetZ
    [o.width + t * 2, t, 0, hd],          // back
    [t, o.depth, -hw, 0],                 // left
    [t, o.depth, hw, 0],                  // right
  ];
  for (const [w, d, ox, oz] of walls) {
    batcher.add(new THREE.BoxGeometry(w, o.height, d), o.wall,
      { position: [originX + ox, o.height / 2, originZ + oz] });
  }

  // Front wall, in two pieces either side of the doorway.
  const side = (o.width - o.doorWidth) / 2;
  for (const sign of [-1, 1]) {
    batcher.add(new THREE.BoxGeometry(side, o.height, t), o.wall, {
      position: [originX + sign * (o.doorWidth / 2 + side / 2), o.height / 2, originZ - hd],
    });
  }
  // A lintel over the doorway, so the gap reads as a door rather than as a
  // wall that someone forgot to finish.
  batcher.add(new THREE.BoxGeometry(o.doorWidth, o.height - 2.15, t), o.wall, {
    position: [originX, o.height - (o.height - 2.15) / 2, originZ - hd],
  });

  root.add(batcher.build());

  // Colliders: the walls, as four boxes. The doorway is deliberately left
  // open - the player walks out through it.
  const wall = 0.3;
  colliders.push(
    { minX: originX - hw - wall, maxX: originX + hw + wall,
      minZ: originZ + hd - wall, maxZ: originZ + hd + wall, top: o.height },
    { minX: originX - hw - wall, maxX: originX - hw + wall,
      minZ: originZ - hd, maxZ: originZ + hd, top: o.height },
    { minX: originX + hw - wall, maxX: originX + hw + wall,
      minZ: originZ - hd, maxZ: originZ + hd, top: o.height },
  );
  for (const sign of [-1, 1]) {
    const cx = originX + sign * (o.doorWidth / 2 + side / 2);
    colliders.push({
      minX: cx - side / 2, maxX: cx + side / 2,
      minZ: originZ - hd - wall, maxZ: originZ - hd + wall, top: o.height,
    });
  }
}

/** A warm bulb, so a room is lit from inside rather than by the sun. */
function roomLight(x: number, y: number, z: number, intensity: number): THREE.PointLight {
  // Warm, but not as warm as a street lamp. The first pass used the lamp's
  // own 0xffd9a8 at a steep decay and turned a beige room the colour of
  // varnish - indoors the bulb is most of the light there is, so its tint is
  // the room's tint.
  const light = new THREE.PointLight(0xffeacc, intensity, 18, 1.5);
  light.position.set(x, y, z);
  return light;
}

// ---------------------------------------------------------------------------
// The rented room
// ---------------------------------------------------------------------------

/**
 * 14B Maple St: one rented room.
 *
 * The brief asks it to say "I am starting from the bottom", so the read is
 * scale and emptiness rather than squalor: a single room with a bed, a table
 * that is also the desk and the kitchen, and a lot of floor with nothing on
 * it. Cheap does not mean filthy, and a room full of clutter would say the
 * opposite of what this one needs to.
 */
function buildHomeRoom(originX: number, originZ: number): InteriorDef {
  const root = new THREE.Group();
  const colliders: Collider[] = [];
  const width = 7.4;
  const depth = 6.2;
  const height = 2.65;

  const floorMat = standard(0x8a7355, { rough: 0.82 });
  const wallMat = standard(0xcfc7b8, { rough: 0.95 });
  const ceilingMat = standard(0xe2ded4, { rough: 1 });

  buildShell(root, colliders, originX, originZ, {
    width, depth, height, floor: floorMat, wall: wallMat, ceiling: ceilingMat,
    doorWidth: 1.2,
  });

  const batcher = new MeshBatcher();
  const woodMat = standard(0x6b5136, { rough: 0.7 });
  const metalMat = standard(0x8d939b, { rough: 0.4, metal: 0.6 });
  const fabricMat = standard(0x4a5a6b, { rough: 0.95 });
  const paleMat = standard(0xb8b0a2, { rough: 0.9 });

  // --- bed, against the back-left corner -----------------------------------
  const bedX = originX - width / 2 + 1.25;
  const bedZ = originZ + depth / 2 - 1.4;
  batcher.add(new THREE.BoxGeometry(1.9, 0.28, 2.0), woodMat,
    { position: [bedX, 0.26, bedZ], rotation: [0, Math.PI / 2, 0] });
  batcher.add(new THREE.BoxGeometry(1.8, 0.22, 1.9), fabricMat,
    { position: [bedX, 0.51, bedZ], rotation: [0, Math.PI / 2, 0] });
  batcher.add(new THREE.BoxGeometry(0.5, 0.14, 0.34), standard(0xe8e2d6, { rough: 1 }),
    { position: [bedX, 0.66, bedZ + 0.72] });
  colliders.push({
    minX: bedX - 1.05, maxX: bedX + 1.05,
    minZ: bedZ - 0.98, maxZ: bedZ + 0.98, top: 0.6,
  });

  // --- the table that is also the kitchen ----------------------------------
  const tableX = originX + width / 2 - 1.5;
  const tableZ = originZ + depth / 2 - 1.2;
  batcher.add(new THREE.BoxGeometry(1.5, 0.07, 0.85), woodMat,
    { position: [tableX, 0.75, tableZ] });
  for (const sx of [-0.65, 0.65]) {
    for (const sz of [-0.34, 0.34]) {
      batcher.add(new THREE.BoxGeometry(0.07, 0.75, 0.07), woodMat,
        { position: [tableX + sx, 0.375, tableZ + sz] });
    }
  }
  colliders.push({
    minX: tableX - 0.8, maxX: tableX + 0.8,
    minZ: tableZ - 0.5, maxZ: tableZ + 0.5, top: 0.8,
  });

  // One chair. Not two - nobody visits yet, and the single chair says that
  // more plainly than any amount of set dressing.
  const chairX = tableX - 0.2;
  const chairZ = tableZ - 0.85;
  batcher.add(new THREE.BoxGeometry(0.42, 0.06, 0.42), woodMat,
    { position: [chairX, 0.45, chairZ] });
  batcher.add(new THREE.BoxGeometry(0.42, 0.5, 0.06), woodMat,
    { position: [chairX, 0.72, chairZ - 0.18] });
  for (const sx of [-0.17, 0.17]) {
    for (const sz of [-0.17, 0.17]) {
      batcher.add(new THREE.BoxGeometry(0.05, 0.45, 0.05), woodMat,
        { position: [chairX + sx, 0.225, chairZ + sz] });
    }
  }

  // --- kitchen run along the right wall ------------------------------------
  const kitchenX = originX + width / 2 - 0.62;
  batcher.add(new THREE.BoxGeometry(1.05, 0.9, 2.4), paleMat,
    { position: [kitchenX, 0.45, originZ - 0.6] });
  batcher.add(new THREE.BoxGeometry(1.1, 0.06, 2.5), standard(0x5f6167, { rough: 0.5 }),
    { position: [kitchenX, 0.92, originZ - 0.6] });
  // A sink: a shallow recess and a tap.
  batcher.add(new THREE.BoxGeometry(0.5, 0.1, 0.6), standard(0x9aa0a6, { rough: 0.35, metal: 0.5 }),
    { position: [kitchenX, 0.9, originZ - 1.1] });
  batcher.add(new THREE.CylinderGeometry(0.025, 0.025, 0.3, 6), metalMat,
    { position: [kitchenX + 0.22, 1.08, originZ - 1.1] });
  colliders.push({
    minX: kitchenX - 0.6, maxX: kitchenX + 0.6,
    minZ: originZ - 1.85, maxZ: originZ + 0.65, top: 0.95,
  });

  // Under-counter fridge, the small kind that comes with the room.
  batcher.add(new THREE.BoxGeometry(0.6, 0.85, 0.6), standard(0xd8d4cc, { rough: 0.55 }),
    { position: [kitchenX, 0.43, originZ + 1.1] });
  colliders.push({
    minX: kitchenX - 0.35, maxX: kitchenX + 0.35,
    minZ: originZ + 0.75, maxZ: originZ + 1.45, top: 0.9,
  });

  // --- storage: where the business lives before it has premises ------------
  const crateX = originX - width / 2 + 0.9;
  const crateZ = originZ - depth / 2 + 1.5;
  for (const [dy, size] of [[0, 0.66], [0.68, 0.5]] as [number, number][]) {
    batcher.add(new THREE.BoxGeometry(size, 0.62, size), standard(0x7a6647, { rough: 0.9 }),
      { position: [crateX, 0.31 + dy, crateZ] });
  }
  colliders.push({
    minX: crateX - 0.4, maxX: crateX + 0.4,
    minZ: crateZ - 0.4, maxZ: crateZ + 0.4, top: 1.3,
  });

  // --- the bathroom, implied ------------------------------------------------
  // A second door in the left wall, permanently shut. The room is a rented
  // room; the bathroom is down the hall and is not somewhere the game goes.
  batcher.add(new THREE.BoxGeometry(0.08, 2.05, 0.85), standard(0x8a7a66, { rough: 0.7 }),
    { position: [originX - width / 2 + 0.13, 1.02, originZ - 1.6] });
  batcher.add(new THREE.SphereGeometry(0.05, 8, 6), metalMat,
    { position: [originX - width / 2 + 0.2, 1.0, originZ - 1.28] });

  // --- window ---------------------------------------------------------------
  // On the back wall, so the view out is sky rather than the room next door.
  batcher.add(new THREE.BoxGeometry(1.5, 1.05, 0.06),
    standard(0xb9cfe0, { rough: 0.08, metal: 0.3 }),
    { position: [originX + 0.6, 1.55, originZ + depth / 2 - 0.13] });
  batcher.add(new THREE.BoxGeometry(1.72, 1.22, 0.05), standard(0xe6e0d4, { rough: 0.8 }),
    { position: [originX + 0.6, 1.55, originZ + depth / 2 - 0.17] });

  root.add(batcher.build());

  const lights = [
    roomLight(originX, height - 0.35, originZ, 7),
    roomLight(originX + 0.6, 1.7, originZ + depth / 2 - 0.8, 2.2),
  ];
  for (const l of lights) root.add(l);
  // A visible bulb, so the light in the scene has a source in the room.
  const bulbMat = standard(0xfff0d0, { rough: 0.4 });
  bulbMat.emissive = new THREE.Color(0xffd9a8);
  bulbMat.emissiveIntensity = 2.2;
  const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.09, 10, 8), bulbMat);
  bulb.position.set(originX, height - 0.32, originZ);
  root.add(bulb);

  return {
    kind: 'home_room',
    locationId: 'home',
    group: root,
    colliders,
    bounds: {
      minX: originX - width / 2 + 0.6, maxX: originX + width / 2 - 0.6,
      minZ: originZ - depth / 2 + 0.6, maxZ: originZ + depth / 2 - 0.6,
    },
    spawn: { x: originX, z: originZ - depth / 2 + 1.1, facing: 0 },
    cameraDistance: 3.6,
    hotspots: [
      { kind: 'exit', x: originX, z: originZ - depth / 2 + 0.35, radius: 1.3, label: 'Go outside' },
      { kind: 'sleep', x: bedX + 0.9, z: bedZ, radius: 1.5, label: 'Sleep' },
      { kind: 'storage', x: crateX + 0.7, z: crateZ, radius: 1.3, label: 'Your kit' },
    ],
    lights,
  };
}

// ---------------------------------------------------------------------------
// The tool shop
// ---------------------------------------------------------------------------

/**
 * Hardline Tool Depot, from the inside.
 *
 * The point of this room is that buying a mower stops being a menu and starts
 * being a place you walked to. So the merchandise is on shelves at the far
 * end, the counter is between you and the door, and the clerk is behind it.
 */
function buildToolShop(originX: number, originZ: number): InteriorDef {
  const root = new THREE.Group();
  const colliders: Collider[] = [];
  const width = 14;
  const depth = 11;
  const height = 3.6;

  const floorMat = standard(0xb0aca4, { rough: 0.55 });
  const wallMat = standard(0xdcd6c8, { rough: 0.9 });
  const ceilingMat = standard(0x9aa0a6, { rough: 0.9 });

  buildShell(root, colliders, originX, originZ, {
    width, depth, height, floor: floorMat, wall: wallMat, ceiling: ceilingMat,
    doorWidth: 2.4,
  });

  const batcher = new MeshBatcher();
  const shelfMat = standard(0x5f6672, { rough: 0.6, metal: 0.35 });
  const counterMat = standard(0x6b5136, { rough: 0.7 });

  // --- shelving along the back and sides ------------------------------------
  const stockColours = [0xd14b3a, 0x2f7f4f, 0xd8a53a, 0x3a6f9a, 0x8a8f96, 0xb5542a];
  let stockIndex = 0;
  function shelfRun(cx: number, cz: number, length: number, rotated: boolean): void {
    const w = rotated ? 0.6 : length;
    const d = rotated ? length : 0.6;
    for (const y of [0.42, 1.06, 1.7]) {
      batcher.add(new THREE.BoxGeometry(w, 0.06, d), shelfMat, { position: [cx, y, cz] });
      // Stock: boxes of varying size, so a shelf reads as full rather than as
      // a shelf. Deterministic rather than random - a shop that restocks
      // differently every time you walk in is unsettling.
      const count = Math.max(2, Math.floor(length / 0.75));
      for (let i = 0; i < count; i++) {
        const t = (i + 0.5) / count - 0.5;
        const size = 0.24 + ((stockIndex * 37) % 11) / 42;
        const colour = stockColours[stockIndex % stockColours.length]!;
        stockIndex++;
        batcher.add(
          new THREE.BoxGeometry(size, size * 1.15, size * 0.8),
          standard(colour, { rough: 0.8 }),
          {
            position: [
              cx + (rotated ? 0 : t * length),
              y + 0.03 + size * 0.58,
              cz + (rotated ? t * length : 0),
            ],
          },
        );
      }
    }
    // Uprights.
    for (const t of [-0.5, 0.5]) {
      batcher.add(new THREE.BoxGeometry(rotated ? 0.6 : 0.08, 2.05, rotated ? 0.08 : 0.6),
        shelfMat, {
          position: [
            cx + (rotated ? 0 : t * length), 1.02, cz + (rotated ? t * length : 0),
          ],
        });
    }
    colliders.push({
      minX: cx - w / 2 - 0.15, maxX: cx + w / 2 + 0.15,
      minZ: cz - d / 2 - 0.15, maxZ: cz + d / 2 + 0.15, top: 2.05,
    });
  }

  shelfRun(originX - 2.6, originZ + depth / 2 - 0.9, 7.4, false);
  shelfRun(originX - width / 2 + 0.9, originZ + 1.2, 6.0, true);
  shelfRun(originX + width / 2 - 0.9, originZ + 1.2, 6.0, true);

  // --- mowers on the floor, the things you are actually here for ------------
  const mowerColours = [0xc0392b, 0x2f7f4f, 0xd8a53a];
  for (let i = 0; i < 3; i++) {
    const mx = originX + 2.4 + i * 1.9;
    const mz = originZ + depth / 2 - 2.6;
    batcher.add(new THREE.BoxGeometry(0.72, 0.3, 0.95),
      standard(mowerColours[i]!, { rough: 0.5 }), { position: [mx, 0.33, mz] });
    batcher.add(new THREE.BoxGeometry(0.36, 0.26, 0.3), standard(0x3a3d42, { rough: 0.6 }),
      { position: [mx, 0.6, mz + 0.1] });
    // Handle, raked back the way a mower's is.
    batcher.add(new THREE.BoxGeometry(0.05, 0.9, 0.05), standard(0x6b7078, { rough: 0.5 }),
      { position: [mx - 0.3, 0.75, mz - 0.45], rotation: [0.5, 0, 0] });
    batcher.add(new THREE.BoxGeometry(0.05, 0.9, 0.05), standard(0x6b7078, { rough: 0.5 }),
      { position: [mx + 0.3, 0.75, mz - 0.45], rotation: [0.5, 0, 0] });
    for (const sx of [-0.3, 0.3]) {
      for (const sz of [-0.36, 0.36]) {
        batcher.add(new THREE.CylinderGeometry(0.14, 0.14, 0.09, 8),
          standard(0x1f2226, { rough: 0.9 }),
          { position: [mx + sx, 0.14, mz + sz], rotation: [0, 0, Math.PI / 2] });
      }
    }
    colliders.push({
      minX: mx - 0.5, maxX: mx + 0.5,
      minZ: mz - 0.7, maxZ: mz + 0.7, top: 0.9,
    });
  }

  // --- counter --------------------------------------------------------------
  const counterZ = originZ - depth / 2 + 3.2;
  const counterX = originX - 2.0;
  batcher.add(new THREE.BoxGeometry(5.0, 1.02, 0.75), counterMat,
    { position: [counterX, 0.51, counterZ] });
  batcher.add(new THREE.BoxGeometry(5.2, 0.07, 0.9), standard(0x3f434a, { rough: 0.35 }),
    { position: [counterX, 1.05, counterZ] });
  // A till.
  batcher.add(new THREE.BoxGeometry(0.42, 0.26, 0.36), standard(0x33363c, { rough: 0.5 }),
    { position: [counterX + 1.7, 1.21, counterZ] });
  colliders.push({
    minX: counterX - 2.6, maxX: counterX + 2.6,
    minZ: counterZ - 0.5, maxZ: counterZ + 0.5, top: 1.1,
  });

  // Price board on the back wall.
  batcher.add(new THREE.BoxGeometry(3.2, 1.1, 0.06), standard(0x2b2f36, { rough: 0.8 }),
    { position: [originX + 3.4, 2.3, originZ + depth / 2 - 0.2] });
  for (let i = 0; i < 4; i++) {
    batcher.add(new THREE.BoxGeometry(2.4, 0.1, 0.03), standard(0xd8cfa8, { rough: 0.9 }),
      { position: [originX + 3.2, 2.66 - i * 0.24, originZ + depth / 2 - 0.24] });
  }

  root.add(batcher.build());

  // --- the clerk ------------------------------------------------------------
  // A real figure rather than a painted one: the room already costs two draw
  // calls after batching, and a person behind the counter is most of what
  // separates a shop from a warehouse.
  const clerk = buildHumanoid({
    skin: 0xc68863, hair: 0x2b1d14, shirt: 0xb5762a,
    trousers: 0x3a3a42, shoes: 0x2a2a2e, height: 1.74,
  });
  clerk.root.position.set(counterX + 0.6, 0, counterZ + 0.85);
  clerk.root.rotation.y = Math.PI;
  root.add(clerk.root);

  const lights = [
    roomLight(originX - 3.5, height - 0.5, originZ - 1, 14),
    roomLight(originX + 3.5, height - 0.5, originZ - 1, 14),
    roomLight(originX, height - 0.5, originZ + depth / 2 - 2, 16),
  ];
  for (const l of lights) root.add(l);
  // Strip lights, so the illumination has visible fittings.
  const stripMat = standard(0xfff6e2, { rough: 0.4 });
  stripMat.emissive = new THREE.Color(0xfff0d8);
  stripMat.emissiveIntensity = 2;
  for (const sz of [originZ - 2.4, originZ + 1.4]) {
    const strip = new THREE.Mesh(new THREE.BoxGeometry(8.5, 0.1, 0.3), stripMat);
    strip.position.set(originX, height - 0.24, sz);
    root.add(strip);
  }

  return {
    kind: 'tool_shop',
    locationId: 'toolshop',
    group: root,
    colliders,
    bounds: {
      minX: originX - width / 2 + 0.7, maxX: originX + width / 2 - 0.7,
      minZ: originZ - depth / 2 + 0.7, maxZ: originZ + depth / 2 - 0.7,
    },
    spawn: { x: originX, z: originZ - depth / 2 + 1.4, facing: 0 },
    cameraDistance: 6.4,
    hotspots: [
      { kind: 'exit', x: originX, z: originZ - depth / 2 + 0.4, radius: 1.6, label: 'Leave the shop' },
      {
        kind: 'shop_tools', x: counterX, z: counterZ - 1.1, radius: 2.4,
        label: 'Browse equipment',
      },
    ],
    lights,
  };
}
